import * as React from 'react';
import { Global, css } from '@emotion/react';
import {
  BreakpointConsumer,
  BreakpointProvider,
  Draft,
  ThemeProvider,
  ToastProvider,
  theme as resiTheme,
} from '@resi-media/resi-ui';
import { Provider as JotaiProvider, useAtomValue } from 'jotai';
import { BrowserRouter as Router } from 'react-router';
import { ErrorBoundary } from '@studio/components/ErrorBoundary';
import { ErrorBoundaryFallback } from '@studio/components/ErrorBoundaryFallback';
import { UnsupportedBrowserBanner } from '@studio/components/UnsupportedBrowserBanner';
import Loading from '@studio/pages/Loading';
import { useAdminInitialization } from '@studio/storeV2/admin-tools/hooks/useAdminInitialization';
import { useSession } from '@studio/storeV2/authentication/session-store';
import { PageMetaProvider } from './contexts';
import { store, storeKey } from './storeV2/store';
import { reset } from './styles/reset-css';

const AuthenticatedApp = React.lazy(
  () => import(/* webpackChunkName: "authenticatedApp" */ /* webpackPrefetch: true */ './authenticated-app')
);
const UnauthenticatedApp = React.lazy(
  () => import(/* webpackChunkName: "unauthenticatedApp" */ './unauthenticated-app')
);

const AdminInitialization = () => {
  useAdminInitialization();

  return null;
};

const App = () => {
  const jotaiStoreKey = useAtomValue(storeKey);
  const { user } = useSession();

  return (
    <JotaiProvider store={store}>
      <PageMetaProvider>
        <BreakpointProvider queries={resiTheme.mq}>
          <BreakpointConsumer>
            {() => {
              const isDesktopPage = !document.querySelector("meta[content='width=device-width, initial-scale=1.0']");
              return (
                <ThemeProvider
                  theme={{
                    ...resiTheme,
                    mq: {
                      xs: isDesktopPage ? '@media all' : resiTheme.mq.xs,
                      sm: isDesktopPage ? '@media all' : resiTheme.mq.sm,
                      md: isDesktopPage ? '@media all' : resiTheme.mq.md,
                      lg: isDesktopPage ? '@media all' : resiTheme.mq.lg,
                      xl: isDesktopPage ? '@media all' : resiTheme.mq.xl,
                      xxl: isDesktopPage ? '@media all' : resiTheme.mq.xxl,
                    },
                  }}
                >
                  <Global
                    styles={(theme) => css`
                      ${reset}
                      body {
                        text-rendering: optimizeLegibility;
                        background: ${theme.palette.surface.default};
                        font-family: ${theme.typography.fontFamilyBody};
                        margin: 0;
                        overflow: auto;
                        height: 100%;
                      }
                    `}
                  />
                  <React.Suspense fallback={<Loading />}>
                    <ToastProvider
                      customCss={{
                        container: () =>
                          css({
                            maxHeight: 'calc(100vh - 40px)',
                            overflowY: 'auto',
                            justifyContent: 'flex-start',
                            width: '100%',
                            pointerEvents: 'none',
                            '& > *': {
                              pointerEvents: 'auto',
                            },
                          }),
                      }}
                    >
                      <Draft.LocalizationProvider>
                        <ErrorBoundary fallback={(props) => <ErrorBoundaryFallback {...props} />}>
                          <Router>
                            <React.Fragment key={jotaiStoreKey}>
                              <Draft.ModalProvider>
                                <AdminInitialization />
                                <UnsupportedBrowserBanner />
                                {user ? <AuthenticatedApp /> : <UnauthenticatedApp />}
                              </Draft.ModalProvider>
                            </React.Fragment>
                          </Router>
                        </ErrorBoundary>
                      </Draft.LocalizationProvider>
                    </ToastProvider>
                  </React.Suspense>
                </ThemeProvider>
              );
            }}
          </BreakpointConsumer>
        </BreakpointProvider>
      </PageMetaProvider>
    </JotaiProvider>
  );
};

export default App;
